import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Profile, Role, SessionResponse } from '@ambec/shared';
import { supabase } from '../lib/supabase';

// El "modo" con el que un ADMIN eligió entrar (admin o usuario). Vive en sessionStorage: sobrevive
// a recargas de la misma sesión pero se limpia al cerrar sesión → se vuelve a preguntar en cada login.
const MODO_KEY = 'ambec_modo';
const leerModo = (): Role | null => {
  const s = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(MODO_KEY) : null;
  return s === 'admin' || s === 'user' ? s : null;
};

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/** Códigos de error del flujo de autenticación, mapeados a mensajes en la UI. */
export type AuthErrorCode = 'credenciales' | 'inactive' | 'no_profile' | 'red';

export class AuthFlowError extends Error {
  readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode) {
    super(code);
    this.name = 'AuthFlowError';
    this.code = code;
  }
}

interface AuthContextValue {
  /** Sesión Supabase vigente (token JWT incluido). */
  session: Session | null;
  /** Perfil validado por el backend; `null` mientras no se confirme. */
  profile: Profile | null;
  /** `true` mientras se resuelve la sesión inicial o se valida el perfil. */
  loading: boolean;
  /** Último error del flujo (p. ej. cuenta desactivada tras cierre forzado). */
  notice: AuthErrorCode | null;
  clearNotice: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** ¿El perfil real es admin? (dueño/administrador de AMBEC). */
  esAdmin: boolean;
  /** Rol EFECTIVO con el que se navega: para un user es su rol; para un admin, el modo que eligió
   *  al entrar (admin | user), o `null` mientras no elige (⇒ se muestra el selector). */
  rolActivo: Role | null;
  /** El admin elige con qué modo entrar (persiste en la sesión). */
  elegirModo: (r: Role) => void;
  /** Vuelve a preguntar el modo (para el botón "cambiar de vista" de la cabecera). */
  reelegirModo: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Valida la sesión contra el backend y obtiene el perfil de la persona.
 * Lanza `AuthFlowError` con el código correspondiente si el backend rechaza.
 */
async function fetchProfile(accessToken: string): Promise<Profile> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/auth/session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    throw new AuthFlowError('red');
  }

  if (res.ok) {
    const data = (await res.json()) as SessionResponse;
    return data.profile;
  }

  let code: AuthErrorCode = 'red';
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error === 'inactive') code = 'inactive';
    else if (body.error === 'no_profile') code = 'no_profile';
  } catch {
    // Cuerpo no-JSON: se mantiene el error genérico de red/servidor.
  }
  throw new AuthFlowError(code);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<AuthErrorCode | null>(null);
  const [modo, setModo] = useState<Role | null>(leerModo);

  /** Token cuyo perfil ya fue validado, para no repetir el POST en cada evento. */
  const validatedToken = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const sync = async (next: Session | null) => {
      if (cancelled) return;

      if (!next) {
        validatedToken.current = null;
        setSession(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setSession(next);
      if (validatedToken.current === next.access_token) {
        setLoading(false);
        return;
      }

      try {
        const perfil = await fetchProfile(next.access_token);
        if (cancelled) return;
        validatedToken.current = next.access_token;
        setProfile(perfil);
      } catch (err) {
        if (cancelled) return;
        validatedToken.current = null;
        setProfile(null);
        if (err instanceof AuthFlowError && (err.code === 'inactive' || err.code === 'no_profile')) {
          // Cuenta desactivada o sin perfil: se cierra la sesión y se informa.
          setNotice(err.code);
          void supabase.auth.signOut();
        } else {
          setNotice('red');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void supabase.auth.getSession().then(({ data }) => {
      void sync(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, next) => {
      // Se difiere con setTimeout para no bloquear el callback interno de
      // supabase-js (llamar a otros métodos auth dentro de él puede colgarlo).
      setTimeout(() => {
        if (cancelled) return;
        if (event === 'SIGNED_OUT') {
          void sync(null);
        } else if (
          event === 'SIGNED_IN' ||
          event === 'TOKEN_REFRESHED' ||
          event === 'USER_UPDATED'
        ) {
          void sync(next);
        }
      }, 0);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const clearNotice = useCallback(() => setNotice(null), []);

  const elegirModo = useCallback((r: Role) => { sessionStorage.setItem(MODO_KEY, r); setModo(r); }, []);
  const reelegirModo = useCallback(() => { sessionStorage.removeItem(MODO_KEY); setModo(null); }, []);
  const esAdmin = profile?.role === 'admin';
  // Rol efectivo: un user navega con su rol; un admin, con el modo elegido (o null ⇒ selector).
  const rolActivo: Role | null = !profile ? null : profile.role !== 'admin' ? profile.role : modo;

  const signIn = useCallback(async (email: string, password: string) => {
    setNotice(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // Supabase responde 400 con credenciales inválidas; cualquier otro
      // fallo (caída de red, servicio) se reporta como error genérico.
      const esCredenciales =
        error.status === 400 || /invalid login credentials/i.test(error.message);
      throw new AuthFlowError(esCredenciales ? 'credenciales' : 'red');
    }
    // El listener de onAuthStateChange valida el perfil contra el backend;
    // la redirección por rol ocurre cuando `profile` queda disponible.
  }, []);

  const signOut = useCallback(async () => {
    validatedToken.current = null;
    setProfile(null);
    sessionStorage.removeItem(MODO_KEY); setModo(null); // que se vuelva a preguntar en el próximo login
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{ session, profile, loading, notice, clearNotice, signIn, signOut, esAdmin, rolActivo, elegirModo, reelegirModo }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return ctx;
}
